/**
 * Pauses looping CSS animations once their element leaves the viewport.
 *
 * The hero float, the ambient wash and the logo marquee all run forever.
 * Without this they keep the compositor busy while the visitor reads the
 * bottom of the page.
 *
 * Returns a ref for the component to bind, rather than toggling a class here
 * — Vue overwrites `class` wholesale on re-render.
 *
 * Usage:
 *   const el = ref(null)
 *   const { paused } = usePauseOffscreen(el)
 *   <div ref="el" class="float" :class="{ 'is-paused': paused }">
 */
import { ref, onMounted, onBeforeUnmount, unref } from "vue";
import { prefersReducedMotion } from "./useScrollReveal";

export function usePauseOffscreen(target) {
  const paused = ref(false);
  let observer = null;

  onMounted(() => {
    const el = unref(target);
    if (!el || prefersReducedMotion() || !("IntersectionObserver" in window)) {
      return;
    }

    observer = new IntersectionObserver(
      ([entry]) => {
        paused.value = !entry.isIntersecting;
      },
      { threshold: 0 }
    );

    observer.observe(el);
  });

  onBeforeUnmount(() => {
    observer?.disconnect();
    observer = null;
  });

  return { paused };
}
